import type { ReactNode } from 'react';
import { GLYPH } from './glyphs';

/**
 * Показ текста в простой разметке редактора (`RichTextField`; сервер — `lrn/service/rich_text.py`):
 * абзацы разделены пустой строкой; «## » / «### » — подзаголовок, «> » — цитата, «- » / «* » —
 * список, «![подпись](https://…)» — изображение (подписью со ссылкой), «[текст](https://…)» внутри
 * абзаца — ссылка.
 */
const IMAGE = /^!\[([^\]]*)\]\((https:\/\/[^\s)]+)\)$/;
const HEADING = /^#{1,3}\s+/;
const LIST_ITEM = /^[-*•]\s+/;
const LINK = /\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g;

type Block =
  | { type: 'heading'; text: string }
  | { type: 'quote'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'image'; caption: string; url: string }
  | { type: 'paragraph'; text: string };

function blocksOf(text: string): Block[] {
  return text
    .trim()
    .split(/\n\s*\n/)
    .flatMap((chunk): Block[] => {
      const lines = chunk
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const first = lines[0];
      if (!first) return [];
      const image = lines.length === 1 ? IMAGE.exec(first) : null;
      if (image) return [{ type: 'image', caption: image[1] ?? '', url: image[2] ?? '' }];
      if (lines.length === 1 && HEADING.test(first)) {
        return [{ type: 'heading', text: first.replace(HEADING, '') }];
      }
      if (lines.every((line) => line.startsWith('>'))) {
        return [{ type: 'quote', text: lines.map((line) => line.replace(/^>\s*/, '')).join(' ') }];
      }
      if (lines.every((line) => LIST_ITEM.test(line))) {
        return [{ type: 'list', items: lines.map((line) => line.replace(LIST_ITEM, '')) }];
      }
      return [{ type: 'paragraph', text: lines.join(' ') }];
    });
}

/** Ключи без индексов: содержимое блока и номер повтора одинаковых блоков. */
function withKeys<T>(
  items: readonly T[],
  contentOf: (item: T) => string,
): { key: string; item: T }[] {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const content = contentOf(item);
    const count = (seen.get(content) ?? 0) + 1;
    seen.set(content, count);
    return { key: `${content}#${count}`, item };
  });
}

function blockContent(block: Block): string {
  switch (block.type) {
    case 'list':
      return `list:${block.items.join('|')}`;
    case 'image':
      return `image:${block.url}:${block.caption}`;
    default:
      return `${block.type}:${block.text}`;
  }
}

function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const index = match.index ?? 0;
    if (index > last) parts.push(text.slice(last, index));
    parts.push(
      <a
        key={`${index}-${match[2]}`}
        href={match[2]}
        target="_blank"
        rel="noreferrer"
        className="text-wine-bordeaux underline-offset-2 hover:underline"
      >
        {match[1]}
      </a>,
    );
    last = index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function MarkupText({ text }: { text: string }) {
  const blocks = withKeys(blocksOf(text), blockContent);
  return (
    <div className="flex flex-col gap-10 text-[13px] leading-[20px] text-ink-900">
      {blocks.map(({ key, item: block }) => {
        switch (block.type) {
          case 'heading':
            return (
              <h3 key={key} className="m-0 font-display text-[16px] leading-[22px] text-ink-900">
                {inline(block.text)}
              </h3>
            );
          case 'quote':
            return (
              <blockquote
                key={key}
                className="m-0 border-l-2 border-gold-leaf-3 pl-12 text-ink-700 italic"
              >
                {inline(block.text)}
              </blockquote>
            );
          case 'list':
            return (
              <ul key={key} className="m-0 flex list-disc flex-col gap-4 pl-18">
                {withKeys(block.items, (item) => item).map((entry) => (
                  <li key={entry.key}>{inline(entry.item)}</li>
                ))}
              </ul>
            );
          case 'image':
            return (
              <p key={key} className="m-0 text-[12px] text-ink-500">
                {GLYPH.square}{' '}
                <a
                  href={block.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-wine-bordeaux underline-offset-2 hover:underline"
                >
                  {block.caption || block.url}
                </a>
              </p>
            );
          default:
            return (
              <p key={key} className="m-0">
                {inline(block.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
